import React, { useState, useEffect } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import { PlusIcon } from "../components/Icons";
import DownloadMenu from "../components/DownloadMenu";

const fmtCr = (n) => (n == null || isNaN(n) ? "—" : `₹${Number(n).toFixed(2)} Cr`);

// UI labels for project lifecycle status (DB still uses Ongoing / Completed).
const STATUS_LABELS = { Ongoing: "Under Construction", Completed: "Operational" };
const displayStatus = (s) => STATUS_LABELS[s] || s || "";

export default function ProjectsPage({ canEdit, onSelectProject }) {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "", status: "Ongoing", company_id: "", vertical_id: "", actual_year: 2025,
  });
  // Filters
  const [filterProjectId, setFilterProjectId] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");   // "", "Ongoing", "Completed"
  const toast = useToast();

  // Track whether the heavy financials (Revenue / EBIDTA, computed via
  // the full P&L synthesis pipeline) have arrived yet. Light data renders
  // the project cards immediately; financials are merged in afterwards.
  const [financialsLoading, setFinancialsLoading] = useState(true);

  const load = () => {
    // Phase 1 — light: fast list, no per-project synthesis. Shows the
    // cards without the Revenue / EBIDTA numbers (they'll appear once
    // phase 2 lands).
    Promise.all([
      api.getProjects({ light: true }),
      api.getCompanies(),
      api.getVerticals().catch(() => []),
    ])
      .then(([p, c, v]) => {
        setProjects(p);
        setCompanies(c);
        setVerticals(Array.isArray(v) ? v : []);
      })
      .catch(() => toast("Failed to load projects", "error"))
      .finally(() => setLoading(false));

    // Phase 2 — heavy: full project list with synthesized Revenue / EBIDTA.
    // Merge into the existing state by id so the cards update in place.
    setFinancialsLoading(true);
    api.getProjects({ light: false })
      .then((full) => {
        const byId = new Map(full.map((p) => [p.id, p]));
        setProjects((prev) => prev.map((p) => {
          const f = byId.get(p.id);
          return f ? { ...p, total_revenue: f.total_revenue, ebidta: f.ebidta, variable_count: f.variable_count } : p;
        }));
      })
      .catch(() => { /* light view still works; ignore silently */ })
      .finally(() => setFinancialsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newProject.name.trim()) return;
    if (!newProject.vertical_id) {
      toast("Please select a Vertical", "error");
      return;
    }
    try {
      await api.createProject(newProject);
      toast("Project created successfully");
      setShowAdd(false);
      setNewProject({ name: "", status: "Ongoing", company_id: companies[0]?.id || "", vertical_id: "", actual_year: 2025 });
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const ongoingCount = projects.filter((p) => p.status === "Ongoing").length;
  const completedCount = projects.filter((p) => p.status === "Completed").length;

  // Apply filters: Project (exact id match), Company (exact id match),
  // Status (Ongoing/Completed). Blank value = no filter.
  const visibleProjects = projects.filter((p) => {
    if (filterProjectId && p.id !== filterProjectId) return false;
    if (filterCompanyId && p.company_id !== filterCompanyId) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });
  const hasFilter = !!(filterProjectId || filterCompanyId || filterStatus);
  const resetFilters = () => {
    setFilterProjectId(""); setFilterCompanyId(""); setFilterStatus("");
  };

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-16">
        <div className="flex gap-8">
          <span className="badge badge-ongoing" style={{ fontSize: 12, padding: "4px 14px" }}>{ongoingCount} Under Construction</span>
          <span className="badge badge-completed" style={{ fontSize: 12, padding: "4px 14px" }}>{completedCount} Operational</span>
        </div>
        <div className="flex items-center gap-10" style={{ flexWrap: "wrap" }}>
          <DownloadMenu
            filename="projects"
            getData={() => ({
              title: "Projects",
              columns: [
                { key: "name", label: "Name" },
                { key: "status", label: "Status" },
                { key: "company_name", label: "Company" },
                { key: "vertical_name", label: "Vertical" },
                { key: "actual_year", label: "Actual FY" },
                { key: "revenue", label: "Revenue (Cr)" },
                { key: "ebidta", label: "EBIDTA (Cr)" },
                { key: "variable_count", label: "Variables" },
              ],
              rows: visibleProjects.map((p) => ({
                name: p.name,
                status: displayStatus(p.status),
                company_name: p.company_name || "—",
                vertical_name: p.vertical_name || "—",
                actual_year: p.actual_year,
                revenue: p.total_revenue == null ? "" : Number(p.total_revenue).toFixed(2),
                ebidta: p.ebidta == null ? "" : Number(p.ebidta).toFixed(2),
                variable_count: p.variable_count || 0,
                // Provide a "Formula" column with the project's notes when
                // Include Formula is ticked.
                _formula_text: p.notes || "",
              })),
            })} />
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
              <PlusIcon /> Add New Project
            </button>
          )}
        </div>
      </div>

      {/* Filters strip — Project (single project), Company (mapped),
          Status (lifecycle). All optional; blank = show everything. */}
      <div className="card mb-16" style={{ padding: "10px 14px" }}>
        <div className="flex items-center gap-10" style={{ flexWrap: "wrap" }}>
          <span className="text-xxs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
            Filter:
          </span>
          <select className="input select" style={{ width: "auto", minWidth: 200 }}
            value={filterProjectId}
            onChange={(e) => setFilterProjectId(e.target.value)}>
            <option value="">— Any project —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="input select" style={{ width: "auto", minWidth: 200 }}
            value={filterCompanyId}
            onChange={(e) => setFilterCompanyId(e.target.value)}>
            <option value="">— Any company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="input select" style={{ width: "auto", minWidth: 180 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">— Any status —</option>
            <option value="Ongoing">Under Construction</option>
            <option value="Completed">Operational</option>
          </select>
          {hasFilter && (
            <>
              <span className="text-xxs text-dim">
                {visibleProjects.length} of {projects.length} project{projects.length === 1 ? "" : "s"}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset</button>
            </>
          )}
        </div>
      </div>

      <div className="grid-auto">
        {visibleProjects.map((p) => (
          <div key={p.id} className="card clickable" onClick={() => onSelectProject(p.id, p.name)}
            style={{ padding: 14 }}>
            <div className="flex justify-between items-start mb-10">
              <div style={{ minWidth: 0 }}>
                <h3 className="heading fw-600" style={{ fontSize: 15 }}>{p.name}</h3>
                <p className="text-xxs text-muted mt-2">{p.company_name || "Unmapped"}</p>
              </div>
              <span className={`badge ${p.status === "Ongoing" ? "badge-ongoing" : "badge-completed"}`}>{displayStatus(p.status)}</span>
            </div>
            <div className="grid-2">
              <div className="sub-card" style={{ padding: "8px 10px" }}>
                <p className="text-xxs text-muted" style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>Revenue (FY{p.actual_year})</p>
                <p className="mono fw-600 text-accent mt-2" style={{ fontSize: 14 }}>
                  {p.total_revenue == null ? (financialsLoading ? "…" : "—") : fmtCr(p.total_revenue)}
                </p>
              </div>
              <div className="sub-card" style={{ padding: "8px 10px" }}>
                <p className="text-xxs text-muted" style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>EBIDTA (FY{p.actual_year})</p>
                <p className={`mono fw-600 mt-2 ${(p.ebidta || 0) < 0 ? "text-neg" : "text-pos"}`} style={{ fontSize: 14 }}>
                  {p.ebidta == null ? (financialsLoading ? "…" : "—") : fmtCr(p.ebidta)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {visibleProjects.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: 32 }}>
          <p className="text-muted">
            {hasFilter
              ? "No projects match the selected filters."
              : "No projects yet. Click Add New Project to create one."}
          </p>
          {hasFilter && (
            <button className="btn btn-ghost btn-sm mt-12" onClick={resetFilters}>Reset filters</button>
          )}
        </div>
      )}

      {showAdd && (
        <Modal title="Add New Project" onClose={() => setShowAdd(false)}>
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input className="input" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="Enter project name" />
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="input select" value={newProject.status} onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}>
                <option value="Ongoing">Under Construction</option>
                <option value="Completed">Operational</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Vertical <span className="text-accent">*</span></label>
              <select className="input select" value={newProject.vertical_id}
                onChange={(e) => setNewProject({ ...newProject, vertical_id: e.target.value })}>
                <option value="">— Select —</option>
                {verticals.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              {verticals.length === 0 && (
                <p className="text-xxs text-dim mt-4">
                  No verticals registered yet. Add one in Settings → Project ↔ Company / Vertical Mapping first.
                </p>
              )}
            </div>
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group">
              <label className="form-label">Company</label>
              <select className="input select" value={newProject.company_id} onChange={(e) => setNewProject({ ...newProject, company_id: e.target.value })}>
                <option value="">— Select —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Actual Year</label>
              <input className="input" type="number" value={newProject.actual_year} onChange={(e) => setNewProject({ ...newProject, actual_year: parseInt(e.target.value) })} />
            </div>
          </div>
          <ModalActions onCancel={() => setShowAdd(false)} onConfirm={handleAdd} confirmLabel="Create Project"
            disabled={!newProject.name.trim() || !newProject.vertical_id} />
        </Modal>
      )}
    </div>
  );
}